import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Clock, FileText, BarChart2, Shield, MessageSquare, Zap, Calendar, Users, TrendingUp } from 'lucide-react';

const LandingNav = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect for navbar shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productFeatures = [
    {
      icon: Clock,
      title: 'Smart Scheduling',
      description: 'AI-powered shift scheduling',
      link: '/schedule-feature',
      color: 'text-blue-600'
    },
    {
      icon: FileText,
      title: 'Timesheet Management',
      description: 'GPS-enabled time tracking',
      link: '/timesheet-feature',
      color: 'text-green-600'
    },
    {
      icon: BarChart2,
      title: 'Analytics & Insights',
      description: 'Real-time workforce analytics',
      link: '/analytics-feature',
      color: 'text-purple-600'
    },
    {
      icon: Shield,
      title: 'Admin Controls',
      description: 'Centralized management dashboard',
      link: '/admin-feature',
      color: 'text-red-600'
    },
    {
      icon: MessageSquare,
      title: 'Team Communication',
      description: 'In-app messaging & notifications',
      link: '/communication-feature',
      color: 'text-indigo-600'
    },
    {
      icon: Zap,
      title: 'AI Optimization',
      description: 'Predictive scheduling intelligence',
      link: '/ai-feature',
      color: 'text-orange-600'
    }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${scrolled ? 'shadow-lg' : 'shadow-md'}`}>
        <div className="flex items-center justify-between px-6 lg:px-12 py-4 mx-auto">
          
          {/* Logo */}
          <div className="text-2xl font-bold text-blue-600 flex items-center">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src="/shiftrylogo.png" alt="Shiftry Logo" className="w-[150px]" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            
            {/* Products Mega Menu */}
            <div
              className="relative group"
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
            >
              <button className="flex items-center text-gray-700 font-medium hover:text-blue-600 transition-colors">
                Product
                <ChevronDown
                  size={18}
                  className={`ml-1 transform transition-transform duration-200 ${
                    isProductsOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Mega Dropdown */}
              <div
                className={`absolute left-1/2 transform -translate-x-1/2 mt-2 w-[600px] bg-white rounded-2xl shadow-2xl border border-gray-100 transition-all duration-300 ${
                  isProductsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                      Core Features
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {productFeatures.map((feature, index) => {
                      const Icon = feature.icon;
                      return (
                        <Link
                          key={index}
                          to={feature.link}
                          className="group/item p-4 rounded-xl hover:bg-blue-50 transition-all duration-200 border border-transparent hover:border-blue-200"
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded-lg bg-gray-100 group-hover/item:bg-white transition-colors`}>
                              <Icon className={`w-5 h-5 ${feature.color}`} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1 group-hover/item:text-blue-600 transition-colors">
                                {feature.title}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <Link to="/solutions" className="text-gray-700 font-medium hover:text-blue-600 transition-colors">
              Industries
            </Link>
            <Link to="/pricing" className="text-gray-700 font-medium hover:text-blue-600 transition-colors">
              Pricing
            </Link>
            <Link to="/documentation" className="text-gray-700 font-medium hover:text-blue-600 transition-colors">
              Docs
            </Link>
            <Link to="/blog" className="text-gray-700 font-medium hover:text-blue-600 transition-colors">
              Blog
            </Link>
          </nav>

          {/* Desktop Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <button 
              className="text-gray-700 font-medium hover:text-blue-600 transition-colors"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
            <button 
              className="px-5 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 transition-all"
              onClick={() => navigate('/signup')}
            >
              Get Started Free
            </button>
            <button 
              className="px-5 py-2.5 text-white rounded-lg bg-gradient-to-r from-gray-900 via-blue-900 to-blue-600 hover:from-black hover:via-blue-800 hover:to-blue-500 font-medium shadow-lg hover:shadow-xl transition-all"
              onClick={() => navigate('/contact')}
            >
              Book Demo
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden text-gray-700 hover:text-blue-600 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white">
            <div className="px-6 py-4 space-y-4">
              
              {/* Mobile Products Section */}
              <div>
                <button
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                  className="flex items-center justify-between w-full text-gray-700 font-medium"
                >
                  <span>Product</span>
                  <ChevronDown
                    size={18}
                    className={`transform transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isProductsOpen && (
                  <div className="mt-3 ml-4 space-y-2">
                    {productFeatures.map((feature, index) => (
                      <Link
                        key={index}
                        to={feature.link}
                        className="block py-2 text-gray-600 hover:text-blue-600"
                        onClick={() => setMenuOpen(false)}
                      >
                        {feature.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/solutions" className="block text-gray-700 font-medium hover:text-blue-600" onClick={() => setMenuOpen(false)}>
                Industries
              </Link>
              <Link to="/pricing" className="block text-gray-700 font-medium hover:text-blue-600" onClick={() => setMenuOpen(false)}>
                Pricing
              </Link>
              <Link to="/documentation" className="block text-gray-700 font-medium hover:text-blue-600" onClick={() => setMenuOpen(false)}>
                Docs
              </Link>
              <Link to="/blog" className="block text-gray-700 font-medium hover:text-blue-600" onClick={() => setMenuOpen(false)}>
                Blog
              </Link>

              {/* Mobile Action Buttons */}
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <button 
                  className="w-full text-left text-gray-700 font-medium hover:text-blue-600"
                  onClick={() => { setMenuOpen(false); navigate('/login'); }}
                >
                  Sign In
                </button>
                <button 
                  className="w-full px-5 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium text-center"
                  onClick={() => { setMenuOpen(false); navigate('/signup'); }}
                >
                  Get Started Free
                </button>
                <button 
                  className="w-full px-5 py-3 text-white bg-gradient-to-r from-gray-900 via-blue-900 to-blue-600 rounded-lg font-medium text-center"
                  onClick={() => { setMenuOpen(false); navigate('/contact'); }}
                >
                  Book Demo
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
      
      {/* Spacer */}
      <div className="h-[80px] w-full bg-transparent" />
    </>
  );
};

export default LandingNav;
